import { useMemo, useRef, useState, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from './utils';
import { COUNTRIES, getCountryByIso, getFlagUrl, type Country } from './countries';
import './country-picker.css';

/**
 * CountryPicker — modal country selector with flag avatar trigger.
 *
 * Trigger: a 56px cell that mimics a FloatingLabelInput in floating
 * state — leading circular flag avatar, two-line text stack
 * (small label on top, country name below), trailing chevron-down.
 *
 * Modal: same Pattern B background as `AddressAutocomplete` (token-
 * driven, hue-neutral in both themes), with a centered title, a search
 * field, and a scrollable list. The currently selected country renders
 * with a small accent badge over its flag.
 *
 * Why raw Radix Dialog (not the shared `DialogContent` wrapper):
 *   The shared wrapper hardcodes a top-right `X` close button that
 *   would conflict with the visual grammar (no Annulla CTA, dismiss
 *   via outside-click only). Same rationale that drove
 *   `AddressAutocomplete` to use the primitives directly.
 *
 * The flag images are fetched from `flagcdn.com` (see `getFlagUrl`).
 * If they fail to load (network error / CSP block / offline), the
 * avatar falls back to a neutral grey circle so the layout stays
 * stable.
 */

export interface CountryPickerProps {
  /** Floating-style label (e.g. "Paese", "Paese della sede operativa"). */
  label: string;
  /** Current ISO 3166-1 alpha-2 code (uppercase). Empty means "no selection". */
  value: string;
  /** Called with the new ISO 3166-1 alpha-2 code (uppercase). */
  onChange: (iso2: string) => void;
  /** Disable the trigger (e.g. while saving). */
  disabled?: boolean;
  /** Forwarded to the trigger button. */
  className?: string;
}

export function CountryPicker({
  label,
  value,
  onChange,
  disabled,
  className,
}: CountryPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const selected = getCountryByIso(value);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-control w-full items-center gap-2 rounded-md bg-foreground/5 px-3 text-left transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <FlagAvatar iso2={selected?.iso2 ?? null} size="sm" />
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="text-xs text-muted-foreground">{label}</span>
            <span className="truncate text-sm text-foreground">
              {selected?.name ?? '—'}
            </span>
          </div>
          <ChevronDown
            className="h-4 w-4 flex-none text-muted-foreground"
            aria-hidden
          />
        </button>
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-country-modal-overlay
          className="fixed inset-0 z-overlay backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        {/* Height contract: target 32rem so the popup feels stable while
            the user types — without `h-[32rem]` the flex container
            shrinks to fit its filtered children and the modal "jumps"
            on every keystroke. The `max-h-[80vh]` cap kicks in only on
            very short viewports (~ <640px tall). */}
        <DialogPrimitive.Content
          data-country-modal
          className="fixed left-1/2 top-1/2 z-modal flex h-[32rem] max-h-[80vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col gap-3 rounded-2xl border border-border/10 p-4 text-popover-foreground shadow-xl"
        >
          <DialogPrimitive.Title className="text-lg font-semibold text-foreground">
            Paese
          </DialogPrimitive.Title>
          <CountryListBody
            selectedIso={value}
            onSelect={(iso2) => {
              onChange(iso2);
              setOpen(false);
            }}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * List + search body. Lives in its own component so the search state
 * (query) is reset every time the dialog opens — Radix unmounts/
 * remounts Content on close by default, which gives the reset for free.
 */
function CountryListBody({
  selectedIso,
  onSelect,
}: {
  selectedIso: string;
  onSelect: (iso2: string) => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.iso2.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          ref={inputRef}
          type="text"
          placeholder="Cerca"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-10 w-full rounded-md bg-foreground/5 pl-9 pr-3 text-sm text-foreground outline-none transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10"
        />
      </div>

      <ul className="-mr-1 flex flex-1 flex-col overflow-y-auto pr-1">
        {filtered.length === 0 ? (
          <li className="px-3 py-2.5 text-sm text-muted-foreground">
            Nessun paese.
          </li>
        ) : (
          filtered.map((c) => (
            <CountryRow
              key={c.iso2}
              country={c}
              selected={c.iso2 === selectedIso}
              onSelect={() => onSelect(c.iso2)}
            />
          ))
        )}
      </ul>
    </>
  );
}

function CountryRow({
  country,
  selected,
  onSelect,
}: {
  country: Country;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm transition-colors focus:outline-none',
          selected
            ? 'bg-foreground/5 text-foreground'
            : 'text-foreground hover:bg-foreground/10 focus:bg-foreground/10',
        )}
      >
        <FlagAvatar iso2={country.iso2} size="md" selected={selected} />
        <span className="flex-1 truncate font-medium">{country.name}</span>
      </button>
    </li>
  );
}

/**
 * Circular flag avatar with a check overlay when `selected`. Falls
 * back to a neutral grey circle when the network image fails to load
 * (offline / CSP block / 4xx) so the layout doesn't shift.
 */
function FlagAvatar({
  iso2,
  size,
  selected,
}: {
  iso2: string | null;
  size: 'sm' | 'md';
  selected?: boolean;
}): JSX.Element {
  const [failed, setFailed] = useState(false);
  const sizeClass = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8';

  return (
    <div className={cn('relative flex-none', sizeClass)}>
      {iso2 && !failed ? (
        <img
          src={getFlagUrl(iso2)}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full rounded-full object-cover"
        />
      ) : (
        <div className="h-full w-full rounded-full bg-foreground/10" />
      )}
      {selected ? (
        <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-foreground text-background ring-2 ring-background">
          <Check className="h-2.5 w-2.5" aria-hidden strokeWidth={3} />
        </span>
      ) : null}
    </div>
  );
}
